import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useCallback, useMemo } from 'react'

import { useBookingContext } from '@providers/booking/booking_context'
import { i18WithDefault as t } from '@shared/locale'
import { formatNumberAsPrice } from 'components-ui/src/shared/common'
import { daysOfStayFromDates } from 'components-ui/src/shared/invoices'
import type { TPetItemProps } from './types'

export const PetItem = ({ groupIdx, selectedIdx, showPricePerPet = false }: TPetItemProps) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const addOn = bookingCtx.state.cart.groups[groupIdx]['petsAddOn']![selectedIdx]
  const daysOfStayCount = useMemo(
    () => daysOfStayFromDates(bookingCtx.state.startDate, bookingCtx.state.endDate, addOn.availability.pricingStrategy),
    [addOn.availability.pricingStrategy, bookingCtx.state.endDate, bookingCtx.state.startDate]
  )
  const onAddOnFromPet = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      const { currentTarget: t } = ev
      bookingCtx.dispatch({
        type: 'INCREASE_PET_ADDON',
        payload: { addOnId: addOn.offer!.id, groupIdx: t.dataset.group, petId: t.dataset.pet },
      })
    },
    [addOn.offer, bookingCtx]
  )

  const onRemoveAddOnFromPet = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      const { currentTarget: t } = ev

      bookingCtx.dispatch({
        type: 'DECREASE_PET_ADDON',
        payload: { addOnId: addOn.offer!.id, groupIdx: t.dataset.group, petId: t.dataset.pet },
      })
    },
    [addOn.offer, bookingCtx]
  )

  const onEveryDayAddOnFromPet = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      const { currentTarget: t } = ev

      bookingCtx.dispatch({
        type: 'SET_PET_ADDON_VALUE',
        payload: {
          addOnId: addOn.offer!.id,
          groupIdx: t.dataset.group,
          petId: t.dataset.pet,
          value: daysOfStayCount,
        },
      })
    },
    [addOn.offer, bookingCtx, daysOfStayCount]
  )

  const items = bookingCtx.state.cart.groups[groupIdx].pets.map((pet, petIdx) => {
    const petAvail = addOn?.availability?.availabilities?.[petIdx]
    if (!petAvail?.available) {
      return null
    }
    const addOnPet = addOn.pets.find((p) => p.id === pet.id)

    if (!addOnPet) return null

    return (
      <Stack
        key={`pet-${pet.id}`}
        direction="row"
        alignItems="center"
        spacing={3}
        sx={{
          px: 2,
          py: 1.25,
          margin: '0 auto',
          width: '100%',
          backgroundColor: theme.palette!.primary?.shade5,
        }}
      >
        <Typography variant="body2" flexGrow={1}>
          {pet.name}
          {showPricePerPet ? ` (${formatNumberAsPrice(petAvail.price?.rate)})` : ''}
        </Typography>
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Tooltip title={t('addonItemPicker.removeItem')} disableInteractive>
            <IconButton
              onClick={onRemoveAddOnFromPet}
              sx={{
                p: '6px',
                width: 44,
                height: 44,
                borderRadius: 1,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
              }}
              data-group={groupIdx}
              data-pet={addOnPet.id}
            >
              <RemoveIcon color="primary" />
            </IconButton>
          </Tooltip>
          <Box component="span" sx={{ width: '35px', textAlign: 'center' }}>
            {addOnPet.qty}
          </Box>
          <Tooltip title={t('addonItemPicker.addItem')} disableInteractive>
            <IconButton
              onClick={onAddOnFromPet}
              sx={{
                p: '6px',
                width: 44,
                height: 44,
                borderRadius: 1,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
              }}
              data-group={groupIdx}
              data-pet={addOnPet.id}
            >
              <AddIcon color="primary" />
            </IconButton>
          </Tooltip>
          <Button
            variant={addOnPet.qty === daysOfStayCount ? 'contained' : 'outlined'}
            onClick={onEveryDayAddOnFromPet}
            size="small"
            sx={{
              marginLeft: '8px',
              height: '44px',
              minWidth: '108px',
              border: '1px solid',
              py: 1,
            }}
            data-group={groupIdx}
            data-pet={addOnPet.id}
          >
            {addOn.availability.pricingStrategy === 'NIGHTLY'
              ? t('global.label.everynight')
              : t('global.label.everyday')}
          </Button>
        </Stack>
      </Stack>
    )
  })

  return <Box>{items}</Box>
}
